<script setup lang="ts">
interface PricingRow {
  label: string;
  standard: string;
  guest: string;
  savings?: string;
  highlight?: boolean;
}

interface PricingBlock {
  header: string;
  intro?: string;
  note?: string;
  upsell?: string;
  show_savings?: boolean;
  rows: PricingRow[];
}

const props = defineProps<{
  block: PricingBlock;
  compactTop?: boolean;
}>();

const { t } = useI18n();

const showSavings = computed(() =>
  props.block.show_savings !== false &&
  props.block.rows.some((r) => Boolean(r.savings))
);
</script>

<template>
  <section class="relative overflow-hidden bg-slate-50 dark:bg-slate-900/40">
    <div
      v-if="!props.compactTop"
      aria-hidden="true"
      class="pointer-events-none absolute -top-32 -right-16 h-80 w-80 rounded-full bg-emerald-200/25 blur-3xl dark:bg-emerald-500/10"
    />
    <div
      v-if="props.compactTop"
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-amber-200/30 blur-3xl dark:bg-amber-500/10"
    />
    <div
      class="relative mx-auto w-full max-w-6xl px-4 pb-10 md:pb-16"
      :class="props.compactTop ? 'pt-2 md:pt-4' : 'pt-10 md:pt-16'"
    >
      <header class="mb-10 flex flex-col gap-4 md:mb-12">
        <h2
          class="font-display italic text-3xl text-slate-900 md:text-4xl dark:text-white"
        >
          {{ block.header }}
        </h2>
        <div class="h-1 w-16 rounded-full bg-amber-400" />
        <p
          v-if="block.intro"
          class="max-w-3xl font-body text-base leading-relaxed text-slate-600 md:text-lg dark:text-slate-300"
        >
          {{ block.intro }}
        </p>
      </header>

      <div
        class="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800"
      >
        <div
          class="hidden md:grid font-body text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 bg-slate-100/70 dark:bg-slate-800/70 px-6 py-4"
          :class="showSavings ? 'grid-cols-[1.6fr_1fr_1fr_0.8fr]' : 'grid-cols-[1.6fr_1fr_1fr]'"
        >
          <span>{{ t("services.pricing.col_item") }}</span>
          <span class="text-right">{{ t("services.pricing.col_walkin") }}</span>
          <span class="text-right">{{ t("services.pricing.col_guest") }}</span>
          <span v-if="showSavings" class="text-right">{{
            t("services.pricing.col_savings")
          }}</span>
        </div>

        <ul>
          <li
            v-for="(row, i) in block.rows"
            :key="i"
            class="border-t border-slate-200/80 px-6 py-5 transition-colors dark:border-slate-700/60"
            :class="[
              row.highlight
                ? 'bg-amber-50/70 dark:bg-amber-900/10'
                : 'bg-white dark:bg-slate-800',
              i === 0 ? 'border-t-0 md:border-t' : '',
            ]"
          >
            <div
              class="grid grid-cols-1 items-baseline gap-y-2 md:gap-y-0"
              :class="showSavings ? 'md:grid-cols-[1.6fr_1fr_1fr_0.8fr] md:gap-x-4' : 'md:grid-cols-[1.6fr_1fr_1fr] md:gap-x-4'"
            >
              <span
                class="font-display text-lg text-slate-900 md:text-xl dark:text-white"
              >
                {{ row.label }}
              </span>

              <span
                class="flex items-baseline gap-2 md:justify-end md:text-right"
              >
                <span
                  class="font-body text-xs uppercase tracking-[0.18em] text-slate-400 md:hidden dark:text-slate-500"
                >
                  {{ t("services.pricing.col_walkin") }}
                </span>
                <span
                  class="font-body text-base text-slate-500 line-through dark:text-slate-400"
                >
                  {{ row.standard }}
                </span>
              </span>

              <span
                class="flex items-baseline gap-2 md:justify-end md:text-right"
              >
                <span
                  class="font-body text-xs uppercase tracking-[0.18em] text-amber-600 md:hidden dark:text-amber-400"
                >
                  {{ t("services.pricing.col_guest") }}
                </span>
                <span
                  class="font-display text-xl font-semibold text-slate-900 md:text-2xl dark:text-white"
                >
                  {{ row.guest }}
                </span>
              </span>

              <span
                v-if="showSavings"
                class="flex items-baseline gap-2 md:justify-end md:text-right"
              >
                <span
                  class="font-body text-xs uppercase tracking-[0.18em] text-slate-400 md:hidden dark:text-slate-500"
                >
                  {{ t("services.pricing.col_savings") }}
                </span>
                <span
                  v-if="row.savings"
                  class="font-display text-base font-semibold text-emerald-600 md:text-lg dark:text-emerald-400"
                >
                  {{ row.savings }}
                </span>
                <span
                  v-else
                  class="text-sm text-slate-400 dark:text-slate-500"
                >—</span>
              </span>
            </div>
          </li>
        </ul>
      </div>

      <div
        v-if="block.note || block.upsell"
        class="mt-6 space-y-3 md:mt-8"
      >
        <p
          v-if="block.note"
          class="font-body text-sm text-slate-500 dark:text-slate-400"
        >
          {{ block.note }}
        </p>
        <p
          v-if="block.upsell"
          class="inline-flex items-center gap-2 rounded-full bg-amber-50 px-4 py-2 font-body text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
        >
          <svg
            class="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
          </svg>
          {{ block.upsell }}
        </p>
      </div>
    </div>
  </section>
</template>
